/**
 * MCP Prompt Registry
 * Manages reusable AI prompt templates for CRM tasks
 */

import { MCPPrompt, MCPPromptMessage, MCPExecutionContext } from '../types';

interface RegisteredPrompt {
  prompt: MCPPrompt;
  getMessages: (args: Record<string, string>, context: MCPExecutionContext) => Promise<MCPPromptMessage[]>;
}

export class MCPPromptRegistry {
  private prompts: Map<string, RegisteredPrompt> = new Map();
  private initialized: boolean = false;

  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    console.log('📝 Initializing MCP Prompt Registry...');
    
    // Register default prompts
    this.registerDefaultPrompts();
    
    this.initialized = true;
    console.log(`✅ Prompt Registry initialized with ${this.prompts.size} prompts`);
  }

  /**
   * Register default CRM prompts
   */
  private registerDefaultPrompts(): void {
    // Campaign Briefing Prompt
    this.registerPrompt(
      {
        name: 'campaign-briefing',
        description: 'Generates a comprehensive briefing for a Google Ads campaign based on landing page insights.',
        arguments: [
          { name: 'url', description: 'The landing page URL', required: true },
          { name: 'industry', description: 'Business industry', required: false }
        ]
      },
      async (args, context) => {
        return [
          {
            role: 'user',
            content: {
              type: 'text',
              text: `Analyze the landing page at ${args.url}${args.industry ? ` for the ${args.industry} industry` : ''}. 
              Provide a strategic briefing for a Google Ads campaign including target audience, primary USPs, and suggested ad angles.`
            }
          }
        ];
      }
    );

    // Lead Follow-up Prompt
    this.registerPrompt(
      {
        name: 'lead-followup',
        description: 'Drafts a personalized follow-up email for a specific lead.',
        arguments: [
          { name: 'leadId', description: 'The unique ID of the lead', required: true },
          { name: 'tone', description: 'Tone of the email (friendly, professional, etc.)', required: false }
        ]
      },
      async (args, context) => {
        return [
          {
            role: 'user',
            content: {
              type: 'text',
              text: `Draft a ${args.tone || 'professional'} follow-up email for lead ${args.leadId}. 
              The goal is to move them to the next stage in the sales pipeline. 
              Use context from their previous interactions if available via tools.`
            }
          }
        ];
      }
    );

    // Meeting Preparation Prompt
    this.registerPrompt(
      {
        name: 'meeting-prep',
        description: 'Creates a meeting agenda and research summary for an upcoming sales meeting.',
        arguments: [
          { name: 'companyName', description: 'Name of the company', required: true },
          { name: 'objective', description: 'Primary goal of the meeting', required: false }
        ]
      },
      async (args, context) => {
        return [
          {
            role: 'user',
            content: {
              type: 'text',
              text: `Prepare me for a meeting with ${args.companyName}. 
              Objective: ${args.objective || 'Discovery call'}. 
              Provide a research summary of the company and a 30-minute meeting agenda.`
            }
          }
        ];
      }
    );
  }

  /**
   * Register a prompt
   */
  registerPrompt(
    prompt: MCPPrompt,
    getMessages: (args: Record<string, string>, context: MCPExecutionContext) => Promise<MCPPromptMessage[]>
  ): void {
    if (this.prompts.has(prompt.name)) {
      console.warn(`⚠️  Prompt ${prompt.name} is already registered, overwriting...`);
    }

    this.prompts.set(prompt.name, {
      prompt,
      getMessages
    });

    console.log(`✅ Registered prompt: ${prompt.name}`);
  }

  /**
   * List all prompts
   */
  async listPrompts(context: MCPExecutionContext): Promise<MCPPrompt[]> {
    return Array.from(this.prompts.values()).map(rp => rp.prompt);
  }

  /**
   * Get a prompt by name
   */
  async getPrompt(
    name: string,
    args: Record<string, string>,
    context: MCPExecutionContext
  ): Promise<{ messages: MCPPromptMessage[]; description?: string }> {
    const registered = this.prompts.get(name);
    
    if (!registered) {
      throw new Error(`Prompt not found: ${name}`);
    }

    // Validate required arguments
    if (registered.prompt.arguments) {
      for (const arg of registered.prompt.arguments) {
        if (arg.required && !args[arg.name]) {
          throw new Error(`Missing required prompt argument: ${arg.name}`);
        }
      }
    }

    const messages = await registered.getMessages(args, context);
    
    return {
      messages,
      description: registered.prompt.description
    };
  }

  /**
   * Get prompt count
   */
  getPromptCount(): number {
    return this.prompts.size;
  }
}

